import { Trans } from '@lingui/macro'
import {
  AddLocationAltOutlined,
  CalendarMonthOutlined,
  ContentPasteSearchOutlined,
  ExploreOutlined,
  HomeOutlined,
  HowToVoteOutlined,
  InventoryOutlined,
  LocalLibraryOutlined,
  PaidOutlined,
  SettingsOutlined
} from '@mui/icons-material'
import { isNil } from 'lodash'
import {
  ALLOCATION_COMMITTEE_GROUP,
  authRoles, DIRECTORS_ADMIN_GROUP,
  hasRole
} from './auth/authRoles'
import { NavigationItem } from './router/Routes'
import sfOauthConfig, {
  chtcEnvs,
  internalEnvs,
  nonInternalEnvs
} from './services/sfAuth/sfAuthConfig'
import { FUNDING_PATHS } from './views/funding/FundingConstants'
import { GRANTS_PATH } from './views/grants/GrantsConstants'
import { DMAH_PATHS } from './views/internal/DMAH/DMAHConstants'
import { INTERNAL_PATHS } from './views/internal/InternalConstants'
import { RESOURCE_INVENTORY_PATHS } from './views/resources/ResourcesConstants'
import { SAT_PATHS } from './views/self-assessment/SATConstants'
import { SURVEY_PATHS } from './views/surveys/SurveyConstants'
import { VIVRE_EN_VILLE_PATHS } from './views/vivre-en-ville/VivreEnVilleConstants'

export const navigations: NavigationItem[] = [
  {
    name: <Trans>GRANTS_ROUTES_HOMEPAGE_LABEL</Trans>,
    datatut: 'reactour__sidenav__homepage',
    icon: <HomeOutlined className='nav-item-icon' />,
    path: GRANTS_PATH.HOME
  },
  {
    name: <Trans>NAVIGATIONS_APPLY_FOR_FUNDING</Trans>,
    icon: <PaidOutlined className='nav-item-icon' />,
    auth: authRoles.grantee,
    path: FUNDING_PATHS.APPLY,
    functionPermissions: [
      ({ user, organization }) => {
        if (!isNil(organization) && !isNil(organization.id)) {
          return false
        }
        return !sfOauthConfig.isInternal
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_FUNDING</Trans>,
    icon: <PaidOutlined className='nav-item-icon' />,
    auth: authRoles.grantee,
    path: FUNDING_PATHS.APPLY,
    env: nonInternalEnvs,
    functionPermissions: [
      ({ user, organization }) => {
        if (!isNil(organization) && !isNil(organization.id)) {
          return true
        }
        return false
      }
    ],
    children: [
      {
        name: <Trans>NAVIGATIONS_ORGANIZATION_DETAILS</Trans>,
        datatut: 'reactour__sidenav__organization',
        auth: authRoles.grantee,
        path: GRANTS_PATH.ORGANIZATION_DETAILS,
        functionPermissions: [
          ({ isInternal }) => {
            return isInternal !== true
          }
        ]
      },
      {
        name: <Trans>NAVIGATIONS_APPLICATIONS</Trans>,
        datatut: 'reactour__sidenav__applicationlist',
        path: FUNDING_PATHS.APPLICATIONS_LIST,
        auth: authRoles.grantee
      },
      {
        name: <Trans>NAVIGATIONS_GRANTEE_REPORTS</Trans>,
        path: GRANTS_PATH.REPORTS,
        auth: authRoles.grantee
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_COMPLETE_ORGANIZATIONAL_COMPASS</Trans>,
    icon: <ExploreOutlined className='nav-item-icon' />,
    path: SAT_PATHS.SAT_LANDING,
    env: nonInternalEnvs,
    auth: authRoles.grantee,
    functionPermissions: [
      ({ user, organization }) => {
        if (!isNil(organization) && !isNil(organization.id)) {
          return false
        }
        return true
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_ORGANIZATIONAL_COMPASS</Trans>,
    icon: <ExploreOutlined className='nav-item-icon' />,
    path: SAT_PATHS.SAT_LANDING,
    env: nonInternalEnvs,
    auth: authRoles.grantee,
    functionPermissions: [
      ({ user, organization }) => {
        if (!isNil(organization) && !isNil(organization.id)) {
          return true
        }
        return false
      }
    ],
    children: [
      {
        name: <Trans>NAVIGATIONS_ASSESSMENTS</Trans>,
        path: SAT_PATHS.MY_ASSESSMENTS,
        env: chtcEnvs,
        auth: authRoles.grantee
      },
      {
        name: <Trans>NAVIGATIONS_ACTION_PLAN</Trans>,
        path: GRANTS_PATH.ACTION_PLAN,
        env: chtcEnvs,
        auth: authRoles.grantee
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_APPLICATION_LIST</Trans>,
    iconText: 'NI',
    path: INTERNAL_PATHS.PROGRAM_MANAGER_APPLICATION_LIST,
    env: internalEnvs,
    icon: <PaidOutlined className='nav-item-icon' />,
    auth: authRoles.pm
  },
  {
    name: <Trans>NAVIGATIONS_RESOURCES</Trans>,
    icon: <LocalLibraryOutlined className='nav-item-icon' />,
    path: RESOURCE_INVENTORY_PATHS.resourceInventory,
    functionPermissions: [
      ({ user }) =>
        user.groupMemberships?.includes(ALLOCATION_COMMITTEE_GROUP) === true ||
        user.role?.includes('GRANTEE')
    ],
    children: [
      {
        name: <Trans>NAVIGATIONS_MY_SHELF</Trans>,
        path: RESOURCE_INVENTORY_PATHS.resourceInventoryMyShelf,
        env: nonInternalEnvs,
        functionPermissions: [
          ({ user }) =>
            user.groupMemberships?.includes(ALLOCATION_COMMITTEE_GROUP) === true ||
            user.role?.includes('GRANTEE')
        ]
      },
      {
        name: <Trans>NAVIGATIONS_MY_RESOURCE_ENTRIES</Trans>,
        path: RESOURCE_INVENTORY_PATHS.resourceInventoryMyEntries,
        env: nonInternalEnvs,
        functionPermissions: [
          ({ user }) =>
            user.groupMemberships?.includes(ALLOCATION_COMMITTEE_GROUP) === true ||
            user.role?.includes('GRANTEE')
        ]
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_EXTERNAL_REVIEWER</Trans>,
    icon: <ContentPasteSearchOutlined className='nav-item-icon' />,
    env: chtcEnvs,
    auth: authRoles.reviewer,
    functionPermissions: [
      ({ user, organization }) => {
        return (
          hasRole(user.role, authRoles.reviewer) &&
          user.hasExternalReviewProfile
        )
      }
    ],
    children: [
      {
        name: <Trans>NAVIGATIONS_REVIEWER_PROFILE</Trans>,
        path: GRANTS_PATH.REVIEWER_PROFILE
      },
      {
        name: <Trans>NAVIGATIONS_ASSIGNED_REVIEWS</Trans>,
        path: GRANTS_PATH.ASSIGNED_REVIEWS,
        auth: authRoles.reviewer
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_BECOME_EXTERNAL_REVIEWER</Trans>,
    path: GRANTS_PATH.APPLY_TO_EXTERNAL_REVIEWER,
    icon: <ContentPasteSearchOutlined className='nav-item-icon' />,
    env: nonInternalEnvs,
    functionPermissions: [
      ({ user }) => {
        const canAsGrantee =
          !hasRole(user.role, authRoles.reviewer) ||
          user.hasExternalReviewProfile !== true

        const isInAllocationCommittee = hasRole(
          user.groupMemberships,
          [ALLOCATION_COMMITTEE_GROUP]
        )

        return (canAsGrantee || (isInAllocationCommittee && !user.hasExternalReviewProfile)) && !sfOauthConfig.isInternal
      }
    ]
  },
  {
    name: <Trans>VVIVRE_EN_VILLE_NAVBAR_TAB</Trans>,
    icon: <AddLocationAltOutlined className='nav-item-icon' />,
    auth: authRoles.vivre_en_ville,
    children: [
      {
        name: <Trans>VIVRE_EN_VILLE_MAP</Trans>,
        path: VIVRE_EN_VILLE_PATHS.MAP,
        auth: authRoles.vivre_en_ville
      },
      {
        name: <Trans>VIVRE_EN_VILLE_RENTS_TAB</Trans>,
        path: VIVRE_EN_VILLE_PATHS.RENTS,
        auth: authRoles.vivre_en_ville
      }
    ]
  },
  {
    name: <Trans>DMAH_TAB</Trans>,
    icon: <InventoryOutlined className='nav-item-icon' />,
    path: DMAH_PATHS.APPLICATIONS,
    auth: authRoles.DMAH
  },
  {
    name: <Trans>NAVIGATIONS_ALLOCATION_COMMITTEE</Trans>,
    icon: <HowToVoteOutlined className='nav-item-icon' />,
    env: chtcEnvs,
    functionPermissions: [
      ({ user, organization }) => {
        return (
          hasRole(user.role, authRoles.allocationCommittee) || user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)
        )
      }
    ],
    children: [
      {
        name: <Trans>NAVIGATIONS_EXECUTIVE_SUMMARIES</Trans>,
        path: `${GRANTS_PATH.EXECUTIVE_SUMMARIES}/AC`,
        functionPermissions: [
          ({ user, organization }) => {
            return (
              hasRole(user.role, authRoles.allocationCommittee) || user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)
            )
          }
        ]
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_SELECTION_COMMITTEE</Trans>,
    icon: <HowToVoteOutlined className='nav-item-icon' />,
    env: chtcEnvs,
    functionPermissions: [
      ({ user, organization }) => {
        return (
          hasRole(user.role, authRoles.selectionCommittee) || user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)
        )
      }
    ],
    children: [
      {
        name: <Trans>NAVIGATIONS_EXECUTIVE_SUMMARIES</Trans>,
        path: `${GRANTS_PATH.EXECUTIVE_SUMMARIES}/SC`,
        functionPermissions: [
          ({ user, organization }) => {
            return (
              hasRole(user.role, authRoles.selectionCommittee) || user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)
            )
          }
        ]
      }
    ]
  },
  {
    name: <Trans>GRANTS_ROUTES_BOARD_EXECUTIVE_SUMMARIES_LABEL</Trans>,
    icon: <HowToVoteOutlined className='nav-item-icon' />,
    env: chtcEnvs,
    path: `${GRANTS_PATH.EXECUTIVE_SUMMARIES}/BOARD`,
    functionPermissions: [
      ({ user, organization }) => {
        return (
          hasRole(user.role, authRoles.board) || user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)
        )
      }
    ]
  },
  {
    name: <Trans>GRANTS_ROUTES_CMHC_EXECUTIVE_SUMMARIES_LABEL</Trans>,
    icon: <HowToVoteOutlined className='nav-item-icon' />,
    env: chtcEnvs,
    path: `${GRANTS_PATH.EXECUTIVE_SUMMARIES}/CMHC`,
    functionPermissions: [
      ({ user, organization }) => {
        return (
          hasRole(user.role, authRoles.cmhc) || user.groupMemberships.includes(DIRECTORS_ADMIN_GROUP)
        )
      }
    ]
  },
  {
    name: <Trans>NAVIGATIONS_CALENDAR</Trans>,
    icon: <CalendarMonthOutlined className='nav-item-icon' />,
    env: chtcEnvs,
    path: GRANTS_PATH.CALENDAR,
    auth: authRoles.grantee,
    functionPermissions: [
      ({ user, organization }) => {
        if (isNil(organization) || isNil(organization.id)) {
          return false
        }
        return true
      }
    ]
  },

  {
    name: <Trans>NAVIGATIONS_ADMINISTRATOR</Trans>,
    iconText: <SettingsOutlined className='nav-item-icon' />,
    auth: [...authRoles.translator, ...authRoles.editor],
    children: [
      {
        name: <Trans>NAVIGATION_GENERATE_PDF_FOR_APPLICATION_FORM</Trans>,
        path: INTERNAL_PATHS.GENERATE_PDF_FOR_APPLICATION,
        auth: authRoles.admin
      },
      {
        name: <Trans>INTERNAL_ROUTES_CONFIGURE_USER_PERMISSIONS_LABEL</Trans>,
        path: INTERNAL_PATHS.CONFIGURE_USER_PERMISSIONS,
        auth: authRoles.admin
        // env: internalEnvs,
      },
      {
        name: <Trans>INTERNAL_ROUTES_CONFIGURE_FUNDING_STREAMS_LABEL</Trans>,
        path: INTERNAL_PATHS.FUNDING_STREAMS_EDITOR,
        auth: authRoles.admin
      },
      {
        name: <Trans>INTERNAL_ROUTES_PORTAL_CONFIGURATIONS_LABEL</Trans>,
        path: INTERNAL_PATHS.PORTAL_CONFIGURATIONS,
        auth: authRoles.admin
        // env: internalEnvs,
      },
      {
        name: <Trans>INTERNAL_ROUTES_EXPORT_SURVEYS_LABEL</Trans>,
        path: INTERNAL_PATHS.EXPORT_SURVEYS,
        auth: authRoles.admin,
        env: internalEnvs
      },
      {
        name: <Trans>FORMS_ROUTES_FORMS_LABEL</Trans>,
        path: INTERNAL_PATHS.FORMS,
        auth: authRoles.editor
      },
      {
        name: <Trans>SURVEY_ROUTES_SURVEY_SCORE_LABEL</Trans>,
        path: SURVEY_PATHS.SURVEY_TEMPLATES,
        auth: authRoles.editor
      },
      {
        name: <Trans>SURVEY_ROUTES_SCORE_CARDS_LABEL</Trans>,
        path: SURVEY_PATHS.SCORE_CARDS,
        auth: authRoles.admin
      },
      {
        name: <Trans>INTERNAL_ROUTES_TRANSLATIONS_EDITOR_LABEL</Trans>,
        path: INTERNAL_PATHS.TRANSLATIONS_EDITOR,
        auth: authRoles.translator
      },
      {
        name: <Trans>PAGE_TRANSLATIONS</Trans>,
        path: INTERNAL_PATHS.PAGE_TRANSLATIONS,
        auth: authRoles.admin
      },
      {
        name: <Trans>GRANTS_ROUTES_ACTION_TEMPLATES_LABEL</Trans>,
        env: chtcEnvs,
        path: GRANTS_PATH.ACTION_TEMPLATES,
        auth: authRoles.editor
      }
    ]
  }
]
